import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import {
  dropdownPortalPositionEndAligned,
  isDropdownPortalTarget,
} from './dropdownPortal';
import DropdownSearchPortalLayer from './DropdownSearchPortalLayer';

/**
 * Anchored panel (dropdown-style) opened from a trigger; portaled to `document.body` with fixed
 * positioning so parent `overflow-hidden` does not clip it. Closes on outside press and Escape.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {React.RefObject<HTMLElement|null>} props.anchorRef
 * @param {string} [props.title]
 * @param {React.ReactNode} [props.titleIcon]
 * @param {string} [props.ariaLabel] - Accessible name when `title` is not shown (defaults to `title` or "Menu").
 * @param {React.ReactNode} props.children
 * @param {string} [props.id] - Optional id for the panel (e.g. for aria-controls)
 * @param {number} [props.panelWidth=360] - Preferred width in px (capped by viewport)
 * @param {'above'|'below'} [props.placement='below'] - Open above or below the anchor
 * @param {'start'|'end'} [props.align='end'] - Horizontal alignment relative to the anchor
 */
function AnchorDropdownPanel({
  open,
  onClose,
  anchorRef,
  title,
  titleIcon,
  ariaLabel,
  children,
  id,
  panelWidth = 360,
  placement = 'below',
  align = 'end',
}: any) {
  const panelRef = useRef<any>(null);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!open || !anchor) return;
    setPortalStyle(
      dropdownPortalPositionEndAligned(anchor.getBoundingClientRect(), panelWidth, {
        topOffset: 8,
        zIndex: 200,
        placement,
        align,
      })
    );
  }, [open, anchorRef, panelWidth, placement, align]);

  useLayoutEffect(() => {
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      const t = e.target;
      if (panelRef.current?.contains(t)) return;
      if (anchorRef?.current?.contains(t)) return;
      if (isDropdownPortalTarget(t)) return;
      onClose();
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    open ? (
      <DropdownSearchPortalLayer style={portalStyle}>
        <motion.div
          key="anchor-dropdown-panel"
          ref={panelRef}
          id={id}
          role="dialog"
          aria-modal="false"
          aria-label={ariaLabel ?? title ?? 'Menu'}
          initial={{ opacity: 0, scale: 0.95, y: -6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className="rounded-xl border border-dark-border bg-dark-surface shadow-xl ring-white/10 max-h-[min(70vh,32rem)] flex flex-col overflow-hidden"
          style={{ width: '100%', height: '100%' }}
        >
          {(title || titleIcon) && (
            <div className="flex shrink-0 items-center gap-2 border-b border-dark-border px-4 py-3">
              {titleIcon}
              {title ? <h2 className="text-base font-semibold text-slate-100">{title}</h2> : null}
            </div>
          )}
          <div className="min-h-0 overflow-y-auto overscroll-contain p-4">{children}</div>
        </motion.div>
      </DropdownSearchPortalLayer>
    ) : null,
    document.body
  );
}

export default AnchorDropdownPanel;
